/**
 * Modal form for creating a Site. Lives next to the Sites list because it
 * is its only consumer at MVP.
 *
 * The shared `createSiteSchema` requires a `themeJson` carrying
 * `schemaVersion: 1` plus an arbitrary record of theme tokens. Slice 1
 * does not yet expose a theme editor — we send the minimum-viable
 * `{ schemaVersion: 1 }` so the server's strict validator accepts the
 * payload and the operator can move on. Slice 2 introduces the theme
 * editor and lifts the placeholder.
 */

import { useEffect } from "react";
import type { ReactElement } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { z } from "zod";
import type { SiteResponse } from "@ottopilot/shared";
import { siteSlugSchema } from "@ottopilot/shared";
import { useCreateSite } from "../../hooks/useSites";
import { useToast } from "../../components/ToastProvider";
import { makeZodResolver } from "../../lib/zodResolver";

/**
 * The dialog form only collects `name` + `slug` — `themeJson` is filled in
 * by the submit handler. Building this narrower schema (rather than
 * directly using `createSiteSchema`) keeps the form state typed exactly as
 * the rendered fields. We re-use the slug constraint from the shared
 * schema so the rule lives in one place.
 */
const formSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(120),
  slug: siteSlugSchema,
});
type FormValues = z.infer<typeof formSchema>;

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated?: (site: SiteResponse) => void;
};

export function CreateSiteDialog({ open, onClose, onCreated }: Props): ReactElement {
  const create = useCreateSite();
  const toast = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: makeZodResolver(formSchema),
    defaultValues: { name: "", slug: "" },
  });

  // Reset the form whenever the dialog re-opens so leftover state from a
  // previous attempt doesn't bleed into the new one.
  useEffect(() => {
    if (open) reset({ name: "", slug: "" });
  }, [open, reset]);

  const onSubmit = handleSubmit((values) => {
    create.mutate(
      {
        name: values.name,
        slug: values.slug,
        // Slice 1 doesn't expose a theme editor — send a minimal default
        // theme that satisfies the shared `themeJsonV1` schema. Slice 2
        // replaces this with a real theme picker.
        themeJson: {
          schemaVersion: 1,
          fontHeadline: "Fraunces",
          fontBody: "Inter",
          primary: "#2D5BFF",
          background: "#FAFAF7",
          text: "#1C1B17",
          radius: 8,
        },
      },
      {
        onSuccess: (site) => {
          toast.showSuccess(`Created “${site.name}”.`);
          onCreated?.(site);
          onClose();
        },
        onError: (err) => {
          toast.showError(err.message || "Could not create site.");
        },
      },
    );
  });

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!create.isPending) onClose();
      }}
      maxWidth="sm"
      fullWidth
      data-testid="create-site-dialog"
    >
      <DialogTitle>New site</DialogTitle>
      <Stack
        component="form"
        onSubmit={(e) => {
          void onSubmit(e);
        }}
        noValidate
      >
        <DialogContent>
          <Stack spacing={2}>
            {create.isError ? (
              <Alert severity="error" data-testid="create-site-error-alert">
                {create.error.message}
              </Alert>
            ) : null}
            <TextField
              label="Name"
              fullWidth
              autoFocus
              error={Boolean(errors.name)}
              helperText={errors.name?.message ?? "Display name shown to admins."}
              inputProps={{ "data-testid": "create-site-name-input" }}
              {...register("name")}
            />
            <TextField
              label="Slug"
              fullWidth
              error={Boolean(errors.slug)}
              helperText={errors.slug?.message ?? "Lowercase, kebab-case, 3-60 chars."}
              inputProps={{ "data-testid": "create-site-slug-input" }}
              {...register("slug")}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            disabled={create.isPending}
            data-testid="create-site-cancel-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || create.isPending}
            data-testid="create-site-submit-button"
          >
            {create.isPending ? "Creating..." : "Create site"}
          </Button>
        </DialogActions>
      </Stack>
    </Dialog>
  );
}
